/**
 * La ficha de una persona del Público (`/activa/publico/:clave`): quién es, su segmento y sus compras, qué pasó por
 * WhatsApp (API y QR) y por mail, y el historial de todos los canales junto, del más nuevo al más viejo.
 *
 * Se abre por la clave opaca (nunca un celular ni un mail en la URL), o por «b:<id>» de su contacto del oficial (los
 * links de Conversaciones). Los celulares van enmascarados. Desde el 17/10/2026 (la Base de datos salió del menú),
 * la tarjeta del WhatsApp oficial (`ConsentimientoOficial`) trae lo que estaba en la ficha de la Base: el sí y su
 * historial, ver el número completo (deja el rastro de quién lo vio) y la supresión.
 *
 * «Público» vuelve a la tabla con los filtros con los que se abrió la ficha (los manda la tabla en el `state`).
 */

import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowLeft, AtSign, MessageCircle, MessagesSquare, ShoppingBag, UserRound } from "lucide-react";
import { publicoApi } from "../api";
import type { CanalHecho, FichaPublico, Fuente } from "../types";
import { usePedido } from "../../emails/hooks/usePedido";
import { ErrorTarjeta, Esqueleto, Vacio } from "../../wa-oficial/components/estadisticas/comun";
import { R_CONVERSACIONES } from "../../wa-oficial/logic/conversaciones";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { ConsentimientoOficial } from "../components/ConsentimientoOficial";
import { fecha, fechaHora, money, num } from "../../campana-nueva/utils/format";
import { R_PUBLICO, estadoMail, estadoWhatsApp, haceCuanto, iniciales, nombreVisible } from "../logic/publico";

const FUENTE: Record<Fuente, string> = {
  base: "Contactos con consentimiento (WhatsApp oficial)",
  ventas: "Base de ventas",
  reactivacion: "Campañas por QR (análisis de ventas)",
  whatsapp: "WhatsApp oficial",
  emails: "Emails (Snov)",
};

const ICONO: Record<CanalHecho, { icono: React.ReactNode; cls: string }> = {
  whatsapp: { icono: <MessageCircle size={13} />, cls: "bg-emerald-50 text-emerald-700" },
  mail: { icono: <AtSign size={13} />, cls: "bg-sky-50 text-sky-700" },
  compra: { icono: <ShoppingBag size={13} />, cls: "bg-amber-50 text-amber-800" },
};

const VIA: Record<string, string> = { api: "API", qr: "QR", snov: "Snov", smtp: "Mail" };

/** A qué tabla vuelve «Público»: la que abrió la ficha, si es la de Público de esta app; si no, la tabla sin filtros. */
export function volverDe(state: unknown): string {
  const v = (state as { volver?: unknown } | null)?.volver;
  return typeof v === "string" && (v === R_PUBLICO || v.startsWith(`${R_PUBLICO}?`)) ? v : R_PUBLICO;
}

/** Un día guardado como «2026-03-12»: al mediodía, para que no se corra al día anterior por el huso horario. */
const delDia = (d: string) => (d.length === 10 ? `${d}T12:00:00` : d);
/** «12 mar 2026 · hace 7 meses» */
const cuando = (at: string | null | undefined) => (at ? `${fecha(delDia(at))} · ${haceCuanto(at)}` : "—");

/** «2 · hace 3 días»; sin fecha (las respuestas por QR no la guardan), sólo la cantidad; sin ninguna, «nunca». */
const cuantasYCuando = (n: number, at: string | null) => (!n ? "nunca" : at ? `${num(n)} · ${haceCuanto(at)}` : num(n));

function Dato({ etiqueta, valor }: { etiqueta: string; valor: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-slate-50 last:border-0 text-sm">
      <span className="text-slate-500">{etiqueta}</span>
      <span className="text-slate-900 text-right">{valor}</span>
    </div>
  );
}

export default function PersonaPublicoPage() {
  const { clave = "" } = useParams();
  const { state } = useLocation();
  const equipo = useEsEquipoDeenex();
  const ficha = usePedido<FichaPublico>(() => publicoApi.persona(clave), clave);
  const volver = (
    <Link to={volverDe(state)} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-violet-700">
      <ArrowLeft size={14} /> Público
    </Link>
  );

  if (ficha.cargando && !ficha.dato) {
    return <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-4">{volver}<Esqueleto alto="h-64" etiqueta="Cargando la ficha" /></div>;
  }
  if (!ficha.dato) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {volver}
        {ficha.code === "NOT_FOUND" ? <Vacio>{ficha.error || "No encontramos a esa persona."}</Vacio> : <ErrorTarjeta texto={ficha.error || "No pudimos leer la ficha."} onReintentar={ficha.recargar} />}
      </div>
    );
  }

  const { persona: p, historial } = ficha.dato;
  const wa = estadoWhatsApp(p);
  const ml = estadoMail(p);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-5">
      {volver}

      <section className="rounded-2xl border border-slate-100 bg-white p-5">
        <div className="flex flex-wrap items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center font-bold">{iniciales(p.nombre, p.emails[0] || "")}</div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-slate-900 truncate">{nombreVisible(p)}</h1>
            <p className="text-sm text-slate-500">
              {p.segmento ? <>{p.segmento.emoji} {p.segmento.nombre}</> : "Sin segmento"}
              {p.compras?.frecuencia ? <> · {num(p.compras.frecuencia)} compras · {money(p.compras.montoTotal)}</> : null}
              {" · "}
              {p.optIn === true ? "dio su consentimiento" : p.optIn === false ? "dijo que no quiere recibir mensajes" : "sin dato de consentimiento"}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {p.conversacionId && (
              <Link to={`${R_CONVERSACIONES}/${encodeURIComponent(p.conversacionId)}?filtro=todas`}
                className="h-9 px-3 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5">
                <MessagesSquare size={13} /> Ver la conversación
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* La ficha completa (pedido de Alan del 12/10): datos, compras, WhatsApp y mail, todo a la vista. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <section className="rounded-2xl border border-slate-100 bg-white p-5">
          <h2 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5"><UserRound size={14} className="text-violet-600" /> Datos</h2>
          <Dato etiqueta="Celular" valor={p.telefonos.length ? p.telefonos.join(", ") : "—"} />
          <Dato etiqueta="Mail" valor={p.emails.length ? <span className="break-all">{p.emails.join(", ")}</span> : "—"} />
          <Dato etiqueta="Consentimiento" valor={p.optIn === true ? "Sí" : p.optIn === false ? "No quiere recibir mensajes" : "Sin dato"} />
          {p.etiquetas.length > 0 && <Dato etiqueta="Etiquetas" valor={p.etiquetas.join(", ")} />}
          <Dato etiqueta="Está en" valor={p.fuentes.map((f) => FUENTE[f]).join(", ")} />
          <Dato etiqueta="Última actividad" valor={cuando(p.ultimaInteraccionAt)} />
        </section>

        <section className="rounded-2xl border border-slate-100 bg-white p-5" data-testid="ficha-compras">
          <h2 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5"><ShoppingBag size={14} className="text-amber-600" /> Compras</h2>
          <Dato etiqueta="Segmento" valor={p.segmento ? `${p.segmento.emoji ? `${p.segmento.emoji} ` : ""}${p.segmento.nombre}` : "Sin segmento"} />
          {p.segmento && (
            <Dato
              etiqueta="Según"
              valor={p.segmento.origen === "motor" ? `La campaña «${p.segmento.analisis}»` : p.segmento.analisis || "el último análisis"}
            />
          )}
          {p.segmento?.esControl && <Dato etiqueta="Grupo de control" valor="No le mandamos nada de esta campaña: es contra quien se mide." />}
          {p.compras ? (
            <>
              <Dato etiqueta="Compras" valor={num(p.compras.frecuencia)} />
              <Dato etiqueta="Gastó en total" valor={money(p.compras.montoTotal)} />
              {p.compras.frecuencia > 0 && <Dato etiqueta="Ticket promedio" valor={money(Math.round(p.compras.montoTotal / p.compras.frecuencia))} />}
              <Dato etiqueta="Última compra" valor={p.compras.ultimaCompra ? cuando(p.compras.ultimaCompra) : "Nunca compró"} />
              {p.compras.sucursal && <Dato etiqueta="Sucursal" valor={p.compras.sucursal} />}
            </>
          ) : (
            <p className="text-xs text-slate-400 mt-1">No hay compras de esta persona en la base de ventas ni en los análisis de las campañas por QR.</p>
          )}
        </section>

        <section className="rounded-2xl border border-slate-100 bg-white p-5" data-testid="ficha-whatsapp">
          <h2 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5"><MessageCircle size={14} className="text-emerald-600" /> WhatsApp · {wa.texto}</h2>
          <Dato etiqueta="Mensajes que salieron" valor={num(p.wa.enviados)} />
          <Dato etiqueta="Por la API (oficial)" valor={num(p.wa.porApi)} />
          <Dato etiqueta="Por una línea (QR)" valor={num(p.wa.porQr)} />
          <Dato etiqueta="Le llegaron / leyó" valor={`${num(p.wa.entregados)} / ${num(p.wa.leidos)}`} />
          <Dato etiqueta="Escribió" valor={cuantasYCuando(p.wa.respuestas, p.wa.ultimaRespuestaAt)} />
          <Dato etiqueta="Último mensaje" valor={cuando(p.wa.ultimoEnvioAt)} />
          {p.baja.whatsapp && <p className="text-xs font-semibold text-red-700 mt-2">Pidió la baja de WhatsApp.</p>}
        </section>

        <section className="rounded-2xl border border-slate-100 bg-white p-5" data-testid="ficha-mail">
          <h2 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5"><AtSign size={14} className="text-sky-600" /> Mail · {ml.texto}</h2>
          <Dato etiqueta="Mails que salieron" valor={num(p.mail.enviados)} />
          <Dato etiqueta="Aperturas" valor={num(p.mail.aperturas)} />
          <Dato etiqueta="Clics" valor={num(p.mail.clics)} />
          <Dato etiqueta="Contestó" valor={cuantasYCuando(p.mail.respuestas, p.mail.ultimaRespuestaAt)} />
          {p.mail.rebotes > 0 && <Dato etiqueta="Rebotes" valor={num(p.mail.rebotes)} />}
          <Dato etiqueta="Último mail" valor={cuando(p.mail.ultimoEnvioAt)} />
          <Dato etiqueta="Última apertura" valor={cuando(p.mail.ultimaAperturaAt)} />
          {p.baja.mail && <p className="text-xs font-semibold text-red-700 mt-2">Pidió la baja de los mails.</p>}
        </section>
      </div>

      <ConsentimientoOficial baseContactoId={p.baseContactoId} equipo={equipo} baja={p.baja.whatsapp} />

      <section className="rounded-2xl border border-slate-100 bg-white p-5">
        <h2 className="text-sm font-semibold text-slate-800 mb-3">Lo que pasó, en todos los canales</h2>
        {historial.length === 0 ? (
          <Vacio>Todavía no le escribimos por ningún canal.</Vacio>
        ) : (
          <ol className="space-y-2.5" data-testid="historial">
            {historial.map((h, i) => (
              <li key={`${h.at}-${i}`} className="flex gap-3 text-sm">
                <span className={`mt-0.5 h-6 w-6 flex-shrink-0 rounded-full flex items-center justify-center ${ICONO[h.canal].cls}`}>{ICONO[h.canal].icono}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-slate-900">
                    <span className="font-semibold">{h.titulo}</span>
                    {h.via && VIA[h.via] && <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">{VIA[h.via]}</span>}
                  </p>
                  {h.detalle && <p className="text-xs text-slate-500 break-words">{h.detalle}</p>}
                </div>
                {/* de una compra se sabe el día, no la hora */}
                <span className="text-xs text-slate-400 whitespace-nowrap">{h.canal === "compra" ? fecha(h.at) : fechaHora(h.at)}</span>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
